import * as React from 'react';
import {identity, emptyArray} from '@packages/utilities/fp/utils';
import {omit} from '@packages/utilities/key-map';

import {emotionStyled} from '../emotion';
import {useTheme} from '../LegacyThemeContext';
import {getDisplayName, forwardHtmlProps} from '../utils';

import type {Styled} from './legacy.types';

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
const propsStyle = (styler: any, props) => {
  const newStyle = typeof styler === 'function' ? styler(props) : styler;
  const {style = {}} = props;

  return {
    ...props,
    style: style ? {...style, ...newStyle} : newStyle,
  };
};

const omitTheme = omit(['theme']);

const _styled = (Component: any) => (styler: any, variantMap: any) => {
  const isHTMLElement = typeof Component === 'string';
  const isBasicStyledComponent = typeof styler === 'object' && !variantMap;
  const isSafeForBasicEmotionStyledUsaged =
    isHTMLElement && isBasicStyledComponent;

  /**
   * Part of the emotionStyled roll out.
   *
   * We're swapping the basic cases of styled/styleDiv usage to render out
   * emotionStyled instead.
   *
   * This is to ensure that it's:
   *
   * 1. Stable for production.
   * 2. Easy to revert (if needed).
   *
   * @see https://webflow.atlassian.net/wiki/spaces/FOUN/pages/142181456
   * @see https://webflow.atlassian.net/browse/PDTY-3373
   */
  if (isSafeForBasicEmotionStyledUsaged) {
    // TODO: Remove this once we have better typing for legacy Styled.
    return emotionStyled(Component as any)(styler);
  }

  // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
  const computeBaseStyles = (props) => propsStyle(styler, props);

  const variantEntries = variantMap
    ? (Object.entries(variantMap) as any)
    : emptyArray;

  const computeVariantStyles = variantEntries.reduce(
    // @ts-expect-error - TS7006 - Parameter 'variantStyler' implicitly has an 'any' type.
    (variantStyler, [variantName, variant]: [any, any]) =>
      // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
      (props) => {
        const prevStyledProps = variantStyler(props);

        if (!props[variantName]) {
          return prevStyledProps;
        }

        return propsStyle(variant, prevStyledProps);
      },
    identity
  );

  const omitInvalidHtmlProps = isHTMLElement
    ? // @ts-expect-error - TS7006 - Parameter '_props' implicitly has an 'any' type.
      (_props) => forwardHtmlProps(_props, Component)
    : identity;

  const computeProps = (props: any) =>
    [
      omitInvalidHtmlProps,
      omitTheme,
      computeVariantStyles,
      computeBaseStyles,
    ].reduceRight((nextProps, fn) => fn(nextProps), props);

  function NewComponent(
    props: any,
    ref:
      | ((arg1: null) => unknown)
      | {
          current: null;
        }
  ) {
    const theme = useTheme();
    return (
      <Component
        {...computeProps({
          ...props,
          theme,
        })}
        ref={ref}
        data-legacy-styled
      />
    );
  }

  NewComponent.displayName = `styled(${getDisplayName(Component)})`;
  // @ts-expect-error - TS2345 - Argument of type '{ (props: any, ref: ((arg1: null) => unknown) | { current: null; }): Element; displayName: string; }' is not assignable to parameter of type 'ForwardRefRenderFunction<null, any>'.
  const MemoComponent = React.memo(React.forwardRef(NewComponent));

  if (process.env.NODE_ENV === 'development') {
    Object.defineProperty(MemoComponent, 'displayName', {
      set(name: string) {
        NewComponent.displayName = `styled(${name})`;
      },
      get() {
        return NewComponent.displayName;
      },
    });
  }

  return MemoComponent;
};

/**
 * @deprecated use `emotionStyled` from `@packages/utilities/styled` instead
 */
export const styled: Styled = _styled as any;

/**
 * @deprecated use `View` from `@packages/systems/spring` or `emotionStyled` from `@packages/utilities/styled` instead
 */
export const styledDiv = styled('div');
